import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  COLOR_PRIMARY,
  COLOR_RED,
  COLOR_LIGHT_RED,
} from "../../constants/color";
import { CheckboxIcon } from "./icons/CheckboxIcon";
import { ErrorIcon } from "./icons/input/ErrorIcon";

type CheckboxVariant = "filled" | "empty";

type Props = {
  name: string;
  variant: CheckboxVariant;
  checked?: boolean;
  label?: string;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  hasError?: boolean;
  errorMessage?: string;
  isSomeSelected?: boolean;
};

export const Checkbox: React.FC<Props> = ({
  name,
  label,
  checked,
  variant,
  disabled,
  onChange,
  hasError,
  errorMessage,
  isSomeSelected,
}) => {
  const formContext = useFormContext();
  const { setValue, watch } = formContext ?? {};

  const [isChecked, setIsChecked] = useState<boolean>(checked ?? false);
  const prevCheckedRef = useRef(checked);

  useEffect(() => {
    if (checked !== undefined) {
      setIsChecked(checked);
    }
  }, [checked]);

  // Sync local state when form value changes
  useEffect(() => {
    if (formContext && name) {
      const subscription = watch?.((value, { name: changedName }) => {
        if (changedName === name && value[name] !== undefined) {
          setIsChecked(value[name]);
        }
      });
      return () => subscription?.unsubscribe?.();
    }
  }, [formContext, name, watch]);

  // Initialize form value on mount if not already set
  useEffect(() => {
    if (formContext && name) {
      const currentValue = watch?.(name);
      if (currentValue === undefined && checked !== undefined) {
        setValue?.(name, checked, { shouldValidate: true });
      } else if (currentValue !== undefined) {
        setIsChecked(currentValue);
      }
    }
  }, [formContext, name, checked, setValue, watch]);

  const handleOnClick = useCallback(() => {
    if (disabled) return;

    const newValue = !isChecked;

    // Update local state immediately for instant feedback
    setIsChecked(newValue);

    // Update form value if in form context
    if (formContext) {
      setValue?.(name, newValue, { shouldValidate: true });
    }

    onChange?.(newValue);
  }, [isChecked, disabled, formContext, name, setValue, onChange]);

  const fillColor = useMemo(() => {
    if (disabled) return "#e6e6e6";

    if (hasError) return COLOR_LIGHT_RED;

    if (isChecked) return COLOR_PRIMARY;

    return "#E6E6E6";
  }, [disabled, isChecked, hasError]);

  return (
    <StyledCheckboxContainer
      $variant={variant}
      $isChecked={isChecked}
      $disabled={disabled}
      $hasError={hasError}
      onClick={handleOnClick}
    >
      <StyledCheckboxContent>
        {!isSomeSelected && <CheckboxIcon fillColor={fillColor} />}

        <StyledLabel
          $variant={variant}
          $isChecked={isChecked}
          $disabled={disabled}
          $hasError={hasError}
        >
          {label}
        </StyledLabel>
      </StyledCheckboxContent>

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}
    </StyledCheckboxContainer>
  );
};

const StyledCheckboxContainer = styled.div<{
  $variant: CheckboxVariant;
  $isChecked: boolean;
  $disabled?: boolean;
  $hasError?: boolean;
}>`
  width: fit-content;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: border-color 0.2s ease, background-color 0.2s ease;
  user-select: none;
  ${({ $variant, $isChecked, $disabled, $hasError }) =>
    $variant === "filled" &&
    `
      padding: 14px 20px;
      border-radius: 8px;
      border: 1px solid ${$disabled
      ? "#CCCCCC"
      : $hasError
        ? COLOR_RED
        : $isChecked
          ? COLOR_PRIMARY
          : "#CCCCCC"
    };
      background-color: ${$disabled ? "#F2F2F2" : "#FFFFFF"};

      &:hover {
        border: 1px solid ${$disabled ? "#CCCCCC" : $hasError ? COLOR_RED : COLOR_PRIMARY
    };
      }
     
  `}
`;

const StyledLabel = styled.div<{
  $variant: CheckboxVariant;
  $isChecked: boolean;
  $disabled?: boolean;
  $hasError?: boolean;
}>`
  color: ${({ $variant, $isChecked, $disabled, $hasError }) =>
    $disabled
      ? "#999999"
      : $hasError
        ? $variant === "filled"
          ? COLOR_RED
          : "#333333"
        : $isChecked
          ? COLOR_PRIMARY
          : "#333333"};
  font-size: 14px;
  font-weight: ${({ $variant }) => ($variant === "filled" ? 700 : 400)};
  line-height: 100%;
  transition: color 0.2s ease;
`;

const StyledCheckboxContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledErrorIconContainer = styled.div`
  margin: 3px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${COLOR_RED};
`;
